/**
 * SurveyForm — authenticated Employee Survey form.
 *
 * Adapted from the public CustomerFeedbackForm (frontend/src/pages/Public/CustomerFeedbackForm.tsx),
 * with two key differences:
 *   1. Requires authentication — reached via a normal in-app route, identified by
 *      the logged-in user (no token-in-URL public link mechanism).
 *   2. Renders 5 question types (Rating, Multiple Choice, Checkbox, Yes/No, Text)
 *      instead of only star ratings, since HR-authored templates can mix types.
 *
 * Responses are completely anonymous — HR never sees which employee submitted
 * which answers, only aggregated statistics.
 */

import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import LockIcon from "@mui/icons-material/Lock";
import PollIcon from "@mui/icons-material/Poll";
import EventIcon from "@mui/icons-material/Event";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BlockIcon from "@mui/icons-material/Block";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";

import { useCustomToast } from "../../../components/Methods/SnackBar";
import { fetchData, postData } from "../../../store/actions";
import { mySurveyUrl, submitMySurveyUrl, draftMySurveyUrl } from "../../../Service/Apis";
import WarningIcon from '@mui/icons-material/Warning';

// Coloured left-border accent, cycled per question — mirrors the per-topic
// accent colours used in the Peer Review form for visual consistency.
const QUESTION_COLORS = ["#1976d2", "#388e3c", "#f57c00", "#7b1fa2", "#c62828", "#00838f"];

const QUESTION_TYPE_LABELS: Record<string, string> = {
  rating: "Rating",
  multiple_choice: "Single Choice",
  checkbox: "Checkbox",
  yes_no: "Yes / No",
  text: "Text",
};

// ── Types ────────────────────────────────────────────────────────────────────

type QuestionType = "rating" | "multiple_choice" | "checkbox" | "yes_no" | "text";

interface OptionData {
  option_id: string;
  label: string;
  order: number;
}

interface QuestionData {
  question_id: string;
  question_text: string;
  question_type: QuestionType;
  rating_scale: number | null;
  rating_style: string | null;
  is_mandatory: boolean;
  order: number;
  options: OptionData[];
}

interface DraftAnswer {
  question_id: string;
  rating_value: number | null;
  text_value: string;
  yes_no_value: boolean | null;
  selected_option_ids: string[];
}

interface SurveyFormData {
  cycle_id: string;
  cycle_name: string;
  description?: string;
  start_date?: string | null;
  end_date: string | null;
  question_count?: number;
  has_draft?: boolean;
  questions: QuestionData[];
  draft_answers: DraftAnswer[] | null;
}

type PageState = "loading" | "intro" | "form" | "success" | "no_survey" | "already_submitted" | "error";

interface AnswerState {
  rating_value: number | null;
  text_value: string;
  yes_no_value: boolean | null;
  selected_option_ids: string[];
}

// ── RatingScaleSelector — rounded numbered options instead of stars ────────

// (rating_style, rating_scale) -> {point: anchor label}. Mirrors
// SurveyQuestion.RATING_ANCHOR_LABELS on the backend — kept in sync manually
// since these labels are purely a display concern and rarely change.
const RATING_ANCHOR_LABELS: Record<string, Record<number, string>> = {
  "agreement_5": { 1: "Strongly Disagree", 2: "Disagree", 3: "Neutral", 4: "Agree", 5: "Strongly Agree" },
  "agreement_3": { 1: "Disagree", 2: "Neutral", 3: "Agree" },
  "happiness_3": { 1: "Not Happy", 2: "Neutral", 3: "Happy" },
  "frequency_3": { 1: "No", 2: "Sometimes", 3: "Yes" },
};

interface RatingScaleSelectorProps {
  scale: number;
  style: string | null;
  value: number;
  onChange: (v: number) => void;
  accentColor: string;
}

/**
 * Renders a row of rounded, numbered buttons (1..scale) for a `rating`
 * question, replacing the old star-based input. Each number is labelled
 * with its anchor caption (e.g. Strongly Disagree .. Strongly Agree, or
 * Not Happy .. Happy) looked up from `RATING_ANCHOR_LABELS` via the
 * question's (rating_style, rating_scale) pair, so respondents don't need
 * to guess what each end of the scale means.
 */
const RatingScaleSelector: React.FC<RatingScaleSelectorProps> = ({ scale, style, value, onChange, accentColor }) => {
  const anchors = RATING_ANCHOR_LABELS[`${style ?? "agreement"}_${scale}`];
  return (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
      {Array.from({ length: scale }, (_, i) => i + 1).map((n) => {
        const selected = value === n;
        return (
          <Box
            key={n}
            onClick={() => onChange(n)}
            sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, cursor: "pointer" }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "1.05rem",
                border: "2px solid",
                borderColor: selected ? accentColor : "grey.300",
                bgcolor: selected ? accentColor : "transparent",
                color: selected ? "#fff" : "text.secondary",
                transition: "all 0.15s",
                "&:hover": {
                  borderColor: accentColor,
                  bgcolor: selected ? accentColor : `${accentColor}10`,
                },
              }}
            >
              {n}
            </Box>
            {anchors?.[n] && (
              <Typography
                variant="caption"
                sx={{ fontSize: 10.5, color: selected ? accentColor : "text.primary", fontWeight: selected ? 700 : 500, textAlign: "center", maxWidth: 70, lineHeight: 1.2 }}
              >
                {anchors[n]}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

/**
 * Authenticated Employee Survey form.
 *
 * Drives the full employee-facing flow for a single survey cycle:
 *   1. Loads the current employee's survey via `loadSurvey()` and shows one
 *      of several full-screen states (loading / no active survey / already
 *      submitted / intro / form / success / error) via `pageState`.
 *   2. Renders each question (rating / multiple_choice / checkbox / yes_no /
 *      text) as an editable card, tracking answers in local `answers` state.
 *   3. Supports saving an in-progress draft (`handleSaveDraft`) and final,
 *      one-time submission (`handleSubmit`) — once submitted the cycle can
 *      never be resubmitted by that employee.
 *   4. Keeps the page in sync with the backend even if left open across a
 *      cycle transition, via visibility/focus listeners and 30s polling
 *      (see the comments inside the mount `useEffect`).
 */
const SurveyForm: React.FC = () => {
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();
  const showToast = useCustomToast();

  const [pageState, setPageState] = useState<PageState>("loading");
  const [formData, setFormData] = useState<SurveyFormData | null>(null);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  /** A blank answer for a single question, used to initialise/reset the `answers` map. */
  const emptyAnswer = (): AnswerState => ({
    rating_value: null,
    text_value: "",
    yes_no_value: null,
    selected_option_ids: [],
  });

  // Extracted so we can re-run it both on mount and whenever the tab regains
  // focus/visibility — this protects against a survey tab left open across a
  // cycle transition (e.g. HR closes the current cycle and activates a new
  // one while the employee's tab is idle in the background): without this,
  // the page would keep showing whatever state ("already submitted", "no
  // active survey", etc.) it fetched at mount time forever, since the data
  // fetch only ran once.
  const loadSurvey = (opts: { silent?: boolean } = {}) => {
    if (!opts.silent) setPageState("loading");
    dispatch(fetchData(mySurveyUrl, undefined, { suppressErrorToast: true }))
      .then((res: any) => {
        if (!res) {
          setPageState("no_survey");
          return;
        }
        setFormData(res);
        const initial: Record<string, AnswerState> = {};
        for (const q of res.questions ?? []) {
          initial[q.question_id] = emptyAnswer();
        }
        for (const d of res.draft_answers ?? []) {
          initial[d.question_id] = {
            rating_value: d.rating_value,
            text_value: d.text_value || "",
            yes_no_value: d.yes_no_value,
            selected_option_ids: d.selected_option_ids || [],
          };
        }
        setAnswers(initial);
        setPageState((prev) => (opts.silent && prev === "form" ? prev : "intro"));
      })
      .catch((err: any) => {
        const status = err?.response?.status;
        const code = err?.response?.data?.error;
        if (status === 404) setPageState("no_survey");
        else if (status === 410 || code === "already_submitted") {
          setPageState("already_submitted");
          if (!opts.silent) {
            showToast("You have already submitted your response for this survey.", "warning", false);
          }
        } else if (!opts.silent) setPageState("error");
      });
  };

  useEffect(() => {
    loadSurvey();

    // A tab can sit open — fully visible, in the foreground, never switched
    // away from — for a long time while HR closes the current cycle and
    // activates a new one underneath it. Neither "visibilitychange" nor
    // "focus" fire in that case, so relying on those alone still leaves the
    // page stuck on stale state (as seen: it took a hard refresh to fix).
    // To cover that, poll in the background every 30s while the page is
    // sitting on a "waiting" screen, in addition to refetching on
    // visibility/focus regain for the instant-feeling case.
    const isWaitingState = (s: PageState) =>
      s === "intro" || s === "no_survey" || s === "already_submitted";

    const recheck = () => {
      setPageState((prev) => {
        if (isWaitingState(prev)) loadSurvey({ silent: true });
        return prev;
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") recheck();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", recheck);
    const pollId = window.setInterval(recheck, 30_000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", recheck);
      window.clearInterval(pollId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const el = headerRef.current;
      if (!el) return;
      setScrolled(el.getBoundingClientRect().bottom < 64);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /** Merges `patch` into the stored answer for one question. */
  const updateAnswer = (questionId: string, patch: Partial<AnswerState>) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { ...prev[questionId], ...patch } }));
  };

  /** Resets a single question's answer back to blank (the "Clear" button in each question card). */
  const clearAnswer = (questionId: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: emptyAnswer() }));
  };

  /** Whether `q` currently has a valid, non-empty answer, per its question type. */
  const isAnswered = (q: QuestionData): boolean => {
    const a = answers[q.question_id];
    if (!a) return false;
    switch (q.question_type) {
      case "rating":
        return a.rating_value !== null && a.rating_value > 0;
      case "multiple_choice":
        return a.selected_option_ids.length === 1;
      case "checkbox":
        return a.selected_option_ids.length > 0;
      case "yes_no":
        return a.yes_no_value !== null;
      case "text":
        return a.text_value.trim().length > 0;
      default:
        return false;
    }
  };

  /** Whether every mandatory question has an answer — gates the Submit button. */
  const allMandatoryFilled = (): boolean => {
    if (!formData) return false;
    return formData.questions.every((q) => !q.is_mandatory || isAnswered(q));
  };

  /** Shapes the local `answers` state into the `{ answers: [...] }` payload expected by the draft/submit endpoints. */
  const buildPayload = () => {
    if (!formData) return { answers: [] };
    const payload = formData.questions
      .map((q) => {
        const a = answers[q.question_id];
        if (!a) return null;
        return {
          question_id: q.question_id,
          rating_value: q.question_type === "rating" ? a.rating_value : null,
          text_value: q.question_type === "text" ? a.text_value : "",
          yes_no_value: q.question_type === "yes_no" ? a.yes_no_value : null,
          selected_option_ids:
            q.question_type === "multiple_choice" || q.question_type === "checkbox"
              ? a.selected_option_ids
              : [],
        };
      })
      .filter(Boolean);
    return { answers: payload };
  };

  /** Saves the current in-progress answers as a draft (can be resumed later; does not lock the survey). */
  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      await dispatch(postData(draftMySurveyUrl, buildPayload(), true));
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch {
      showToast("Failed to save draft. Please try again.", "warning", false);
    } finally {
      setSavingDraft(false);
    }
  };

  /** Final, one-time submission — locks the survey for this employee/cycle. */
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      await dispatch(postData(submitMySurveyUrl, buildPayload(), true));
      setPageState("success");
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.error;
      if (status === 410 || code === "already_submitted") {
        setPageState("already_submitted");
        showToast("You have already submitted your response for this survey.", "warning", false);
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Wraps a centered card both horizontally and vertically within the viewport
  // (accounting for the fixed navbar height), matching the intro screen layout.
  const CenteredCard: React.FC<{ children: React.ReactNode; maxWidth?: "sm" | "md" }> = ({ children, maxWidth = "sm" }) => (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        px: 2,
      }}
    >
      <Container maxWidth={maxWidth} disableGutters>
        {children}
      </Container>
    </Box>
  );

  /**
   * Renders a centered, single-message full-screen state (loading / no
   * survey / already submitted / error / success). When `showRetry` is set,
   * adds a "Check again" button that re-runs `loadSurvey()` on demand.
   */
  const renderInfoScreen = (
    icon: React.ReactNode,
    title: string,
    message: string,
    iconBg: string = "grey.200",
    showRetry: boolean = false
  ) => (
    <CenteredCard>
      <Paper
        elevation={0}
        sx={{ p: { xs: 3, sm: 4.5 }, borderRadius: 3, border: "1px solid #e0e0e0", textAlign: "center" }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2.5,
          }}
        >
          {icon}
        </Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>{title}</Typography>
        <Typography color="text.secondary">{message}</Typography>
        {showRetry && (
          <Button
            variant="text"
            size="small"
            onClick={() => loadSurvey()}
            sx={{ mt: 2.5, textTransform: "none", fontWeight: 600 }}
          >
            Check again
          </Button>
        )}
      </Paper>
    </CenteredCard>
  );

  if (pageState === "loading") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "calc(100vh - 64px)" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (pageState === "no_survey") {
    return renderInfoScreen(
      <PollIcon sx={{ fontSize: 34, color: "text.secondary" }} />,
      "No Survey Available",
      "There is no active employee survey for you to complete right now. Please check back later.",
      "grey.200",
      true
    );
  }

  if (pageState === "already_submitted") {
    return renderInfoScreen(
      <CheckCircleIcon sx={{ fontSize: 34, color: "info.dark" }} />,
      "Already Submitted",
      "You have already submitted your response for this survey. Thank you for your feedback!",
      "info.light",
      true
    );
  }

  if (pageState === "error") {
    return renderInfoScreen(
      <BlockIcon sx={{ fontSize: 34, color: "error.dark" }} />,
      "Error",
      "Something went wrong. Please try again later.",
      "error.light"
    );
  }

  if (pageState === "success") {
    return renderInfoScreen(
      <PollIcon sx={{ fontSize: 34, color: "success.dark" }} />,
      "Thank You!",
      "Your response has been recorded anonymously. Your identity will never be shared with HR.",
      "success.light"
    );
  }

  if (!formData) return null;

  if (pageState === "intro") {
    return (
      <CenteredCard>
        <Paper
          elevation={0}
          sx={{ p: { xs: 3, sm: 4.5 }, borderRadius: 3, border: "1px solid #e0e0e0", textAlign: "center" }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "success.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2.5,
            }}
          >
            <PollIcon sx={{ fontSize: 34, color: "success.dark" }} />
          </Box>

          <Typography variant="h5" fontWeight={700} gutterBottom>
            {formData.cycle_name}
          </Typography>

          {formData.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {formData.description}
            </Typography>
          )}

          <Stack spacing={1.25} sx={{ my: 2.5, textAlign: "left" }}>
            {typeof formData.question_count === "number" && (
              <Box display="flex" alignItems="center" gap={1.25}>
                <HelpOutlineIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {formData.question_count} question{formData.question_count !== 1 ? "s" : ""}
                </Typography>
              </Box>
            )}
            {formData.end_date && (
              <Box display="flex" alignItems="center" gap={1.25}>
                <EventIcon fontSize="small" color="action" />
                <Typography variant="body2">Open until {formData.end_date}</Typography>
              </Box>
            )}
            <Box display="flex" alignItems="center" gap={1.25}>
              <LockIcon fontSize="small" color="action" />
              <Typography variant="body2">
                Completely anonymous — HR will never see your name or employee ID.
              </Typography>
            </Box>
          </Stack>

          <Alert icon={<WarningIcon fontSize="medium" />} severity="warning" sx={{ mb: 3, borderRadius: 2, textAlign: "left" }}>
          <strong>Once submitted, your response is final and cannot be edited or resubmitted.
            You can save a draft and come back later before submitting</strong>.
          </Alert>

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => {
              setPageState("form");
              if (formData.has_draft) {
                enqueueSnackbar("Your saved progress has been restored.", { variant: "info", autoHideDuration: 4000 });
              }
            }}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, py: 1.4 }}
          >
            {formData.has_draft ? "Continue Survey →" : "Start Survey →"}
          </Button>
        </Paper>
      </CenteredCard>
    );
  }

  const sortedQuestions = [...formData.questions].sort((a, b) => a.order - b.order);
  const totalQuestions = sortedQuestions.length;
  const answeredCount = sortedQuestions.filter((q) => isAnswered(q)).length;
  const progress = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  return (
    <>
      {/* ── Fixed compact header — slides in when user scrolls past the full header ── */}
      <Box
        sx={{
          position: "fixed",
          top: 64,
          left: 0,
          right: 0,
          zIndex: 1050,
          transform: scrolled ? "translateY(0)" : "translateY(-110%)",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          background: "linear-gradient(135deg, #145A32 0%, #1E7B44 70%, #9DC4AD 100%)",
          boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.3)" : "none",
          pointerEvents: scrolled ? "auto" : "none",
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ py: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton
              size="small"
              onClick={() => setPageState("intro")}
              sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff" } }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>

            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "rgba(255,255,255,0.2)",
                color: "#fff",
                border: "1.5px solid rgba(255,255,255,0.4)",
              }}
            >
              <PollIcon fontSize="small" />
            </Avatar>

            <Box flex={1} sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} color="#fff" noWrap>
                {formData.cycle_name}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.25}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    flex: 1,
                    height: 5,
                    borderRadius: 3,
                    bgcolor: "rgba(255,255,255,0.25)",
                    "& .MuiLinearProgress-bar": { bgcolor: "#fff", borderRadius: 3 },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {answeredCount}/{totalQuestions} ({progress}%)
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: 10, mb: 10 }}>
        {/* ── Full header card — stays in document flow, acts as scroll sentinel ─ */}
        <div ref={headerRef}>
          <Paper elevation={4} sx={{ mb: 3, borderRadius: 3, overflow: "hidden" }}>
            <Box
              sx={{
                background: "linear-gradient(135deg, #145A32 0%, #1E7B44 70%, #9DC4AD 100%)",
                px: 3,
                py: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <IconButton
                onClick={() => setPageState("intro")}
                size="small"
                sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff" } }}
              >
                <ArrowBackIcon />
              </IconButton>

              <Avatar
                sx={{
                  width: 52,
                  height: 52,
                  bgcolor: "rgba(255,255,255,0.2)",
                  border: "2px solid rgba(255,255,255,0.4)",
                }}
              >
                <PollIcon />
              </Avatar>

              <Box flex={1}>
                <Typography variant="h6" fontWeight={700} color="#fff">
                  {formData.cycle_name}
                </Typography>
                {formData.end_date && (
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                    Open until {formData.end_date}
                  </Typography>
                )}
              </Box>

              <Chip
                icon={<LockIcon sx={{ color: "#fff !important" }} fontSize="small" />}
                label="Anonymous"
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  fontWeight: 600,
                  border: "1px solid rgba(255,255,255,0.3)",
                }}
              />
            </Box>

            {/* Progress bar */}
            <Box sx={{ px: 3, py: 1.5, bgcolor: "#f0f7f3" }}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption" color="text.primary" fontWeight={500}>
                  Completion
                </Typography>
                <Typography variant="caption" sx={{ color: "#145A32" }} fontWeight={700}>
                  {answeredCount} / {totalQuestions} &nbsp;({progress}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 7,
                  borderRadius: 4,
                  bgcolor: "#c8e0d0",
                  "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: "#1E7B44" },
                }}
              />
            </Box>
          </Paper>
        </div>

        <Alert
          icon={<LockIcon fontSize="small" />}
          severity="info"
          sx={{ mb: 3, borderRadius: 2 }}
        >
          Your response is completely anonymous. HR will never see your name or employee ID
          attached to any answer — only aggregated, anonymous statistics.
        </Alert>

        {/* ── Questions ──────────────────────────────────────────────────── */}
        <Stack spacing={2.5}>
          {sortedQuestions.map((q, idx) => {
            const a = answers[q.question_id] ?? emptyAnswer();
            const accentColor = QUESTION_COLORS[idx % QUESTION_COLORS.length];
            const answered = isAnswered(q);

            return (
              <Paper
                key={q.question_id}
                elevation={2}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  borderLeft: `4px solid ${accentColor}`,
                }}
              >
                <Box
                  sx={{
                    px: 3,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: `${accentColor}08`,
                    borderBottom: "1px solid",
                    borderColor: "grey.100",
                  }}
                >
                  <Chip
                    label={QUESTION_TYPE_LABELS[q.question_type]}
                    size="small"
                    sx={{
                      bgcolor: `${accentColor}18`,
                      color: accentColor,
                      fontWeight: 600,
                      fontSize: 11,
                    }}
                  />
                  <Box display="flex" alignItems="center" gap={1}>
                    {answered && (
                      <Button
                        size="small"
                        onClick={() => clearAnswer(q.question_id)}
                        startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: "none",
                          color: "text.secondary",
                          minWidth: 0,
                          px: 1,
                          py: 0.25,
                          "&:hover": { color: "error.main", bgcolor: "error.lighter" },
                        }}
                      >
                        Clear
                      </Button>
                    )}
                    {answered ? (
                      <CheckCircleIcon fontSize="small" sx={{ color: accentColor }} />
                    ) : q.is_mandatory ? (
                      <Chip label="Required" size="small" color="error" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                    ) : (
                      <Chip label="Optional" size="small" variant="outlined" sx={{ fontSize: 10, height: 20, color: "text.primary", borderColor: "text.secondary", fontWeight: 600 }} />
                    )}
                  </Box>
                </Box>

                <Box sx={{ px: 3, py: 2.5 }}>
                  <Box display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                    <Box
                      sx={{
                        mt: "1px",
                        minWidth: 26,
                        height: 26,
                        borderRadius: "50%",
                        bgcolor: answered ? accentColor : "grey.200",
                        color: answered ? "#fff" : "text.secondary",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: 700,
                        transition: "all 0.2s",
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Typography variant="body1" fontWeight={700} fontSize="1.02rem">
                      {q.question_text}
                    </Typography>
                  </Box>

                  <Box pl={4.75}>
                    {q.question_type === "rating" && (
                      <RatingScaleSelector
                        scale={q.rating_scale ?? 5}
                        style={q.rating_style}
                        value={a.rating_value ?? 0}
                        onChange={(v) => updateAnswer(q.question_id, { rating_value: v })}
                        accentColor={accentColor}
                      />
                    )}

                    {q.question_type === "multiple_choice" && (
                      <RadioGroup
                        value={a.selected_option_ids[0] ?? ""}
                        onChange={(e) => updateAnswer(q.question_id, { selected_option_ids: [e.target.value] })}
                      >
                        {[...q.options].sort((x, y) => x.order - y.order).map((opt) => (
                          <FormControlLabel
                            key={opt.option_id}
                            value={opt.option_id}
                            control={<Radio sx={{ "&.Mui-checked": { color: accentColor } }} />}
                            label={opt.label}
                          />
                        ))}
                      </RadioGroup>
                    )}

                    {q.question_type === "checkbox" && (
                      <Box display="flex" flexDirection="column">
                        {[...q.options].sort((x, y) => x.order - y.order).map((opt) => {
                          const checked = a.selected_option_ids.includes(opt.option_id);
                          return (
                            <FormControlLabel
                              key={opt.option_id}
                              control={
                                <Checkbox
                                  checked={checked}
                                  sx={{ "&.Mui-checked": { color: accentColor } }}
                                  onChange={() => {
                                    const next = checked
                                      ? a.selected_option_ids.filter((id) => id !== opt.option_id)
                                      : [...a.selected_option_ids, opt.option_id];
                                    updateAnswer(q.question_id, { selected_option_ids: next });
                                  }}
                                />
                              }
                              label={opt.label}
                            />
                          );
                        })}
                      </Box>
                    )}

                    {q.question_type === "yes_no" && (
                      <Box display="flex" gap={1.5}>
                        <Chip
                          label="Yes"
                          clickable
                          color={a.yes_no_value === true ? "success" : "default"}
                          variant={a.yes_no_value === true ? "filled" : "outlined"}
                          onClick={() => updateAnswer(q.question_id, { yes_no_value: true })}
                        />
                        <Chip
                          label="No"
                          clickable
                          color={a.yes_no_value === false ? "error" : "default"}
                          variant={a.yes_no_value === false ? "filled" : "outlined"}
                          onClick={() => updateAnswer(q.question_id, { yes_no_value: false })}
                        />
                      </Box>
                    )}

                    {q.question_type === "text" && (
                      <TextField
                        multiline
                        minRows={3}
                        fullWidth
                        placeholder="Type your response…"
                        value={a.text_value}
                        onChange={(e) => updateAnswer(q.question_id, { text_value: e.target.value })}
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Stack>

        <Collapse in={draftSaved}>
          <Alert severity="success" sx={{ mt: 2.5, borderRadius: 2 }}>
            Draft saved! Your progress will be restored the next time you open this survey.
          </Alert>
        </Collapse>

        {submitError && (
          <Typography color="error" sx={{ mt: 2 }}>{submitError}</Typography>
        )}

        <Divider sx={{ my: 3 }} />

        {/* ── Action buttons ────────────────────────────────────────────── */}
        <Box
          display="flex"
          gap={2}
          alignItems="center"
          flexDirection={{ xs: "column", sm: "row" }}
          sx={{
            position: "sticky",
            bottom: 16,
            zIndex: 10,
            bgcolor: "background.paper",
            px: 2,
            py: 1.5,
            borderRadius: 3,
            boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flex: 1, order: { xs: 3, sm: 0 } }}
          >
            {answeredCount} of {totalQuestions} questions answered
          </Typography>
          <Button
            variant="outlined"
            size="large"
            fullWidth={true}
            onClick={handleSaveDraft}
            disabled={submitting || savingDraft}
            startIcon={savingDraft ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", width: { xs: "100%", sm: "auto" } }}
          >
            {savingDraft ? "Saving..." : "Save as Draft"}
          </Button>

          <Button
            variant="contained"
            size="large"
            disabled={!allMandatoryFilled() || submitting || savingDraft}
            onClick={handleSubmit}
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />
            }
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, width: { xs: "100%", sm: "auto" } }}
          >
            {submitting ? "Submitting..." : "Submit Survey"}
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default SurveyForm;
